package com.evoapps.evoshift.billing

import com.evoapps.evoshift.domain.billing.BillingCustomer
import com.evoapps.evoshift.domain.billing.BillingDuration
import com.evoapps.evoshift.domain.billing.BillingException
import com.evoapps.evoshift.domain.billing.BillingPeriod
import com.evoapps.evoshift.domain.billing.BillingStore
import com.evoapps.evoshift.domain.billing.Entitlement
import com.evoapps.evoshift.domain.billing.IntroductoryOffer
import com.evoapps.evoshift.domain.billing.PlanOffer
import com.evoapps.evoshift.domain.billing.Plans
import com.evoapps.evoshift.domain.repository.BillingRepository
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlin.js.Promise
import kotlin.time.Instant

/** The RevenueCat Web Billing SDK (npm `@revenuecat/purchases-js`, the version EVoLabels uses). */
@JsModule("@revenuecat/purchases-js")
@JsNonModule
external object RevenueCatJs {
    val Purchases: dynamic
    val ErrorCode: dynamic
}

/**
 * Web subscriptions through RevenueCat Web Billing (SH-38). EVoLabels talks to RevenueCat's REST
 * API and its own Stripe functions; here the official purchases-js SDK does both the checkout
 * and the customer info, so no secret ever reaches the browser (the key is RevenueCat's public
 * Web Billing key).
 */
class WebBillingRepository(private val apiKey: String) : BillingRepository {
    override val isConfigured: Boolean = true
    override val store: BillingStore = BillingStore.WEB

    private val _customer = MutableStateFlow(BillingCustomer())
    override val customer: StateFlow<BillingCustomer> = _customer.asStateFlow()

    private var identifiedUid: String? = null

    /** The SDK instance for the identified user; null before [identify]. */
    private fun instance(): dynamic =
        if (identifiedUid != null && RevenueCatJs.Purchases.isConfigured() as Boolean) RevenueCatJs.Purchases.getSharedInstance() else null

    private fun publish(info: dynamic) {
        val uid = identifiedUid ?: return
        if (info == null || info == undefined) return
        if (instance()?.getAppUserId() as? String != uid) return // a previous identity's late answer
        val active = info.entitlements?.active
        val ids = if (active == null || active == undefined) emptyArray() else js("Object.keys")(active).unsafeCast<Array<String>>()
        _customer.value = BillingCustomer(
            uid = uid,
            entitlements = ids.map { id ->
                val e = active[id]
                Entitlement(
                    id = id,
                    productId = e.productIdentifier as? String,
                    expiresAt = epochMillis(e.expirationDate)?.let(Instant::fromEpochMilliseconds),
                    willRenew = (e.willRenew as? Boolean) ?: true,
                    store = BillingStore.fromRevenueCat(e.store as? String),
                    isSandbox = (e.isSandbox as? Boolean) ?: false,
                    hasBillingIssue = e.billingIssueDetectedAt != null && e.billingIssueDetectedAt != undefined
                )
            },
            managementUrl = info.managementURL as? String,
            subscriptionEnds = expirationDates(info.allExpirationDatesByProduct)
        )
    }

    private fun expirationDates(byProduct: dynamic): Map<String, Instant?> {
        if (byProduct == null || byProduct == undefined) return emptyMap()
        return js("Object.keys")(byProduct).unsafeCast<Array<String>>()
            .associateWith { id -> epochMillis(byProduct[id])?.let(Instant::fromEpochMilliseconds) }
    }

    private fun epochMillis(date: dynamic): Long? =
        if (date == null || date == undefined) null else (date.getTime() as Number).toLong()

    override suspend fun identify(uid: String?) {
        identifiedUid = uid
        if (uid == null) {
            _customer.value = BillingCustomer()
            return
        }
        if (_customer.value.uid != uid) _customer.value = BillingCustomer(uid = uid)
        runCatching {
            val purchases = RevenueCatJs.Purchases
            if (!(purchases.isConfigured() as Boolean)) {
                purchases.configure(apiKey, uid)
                publish(purchases.getSharedInstance().getCustomerInfo().unsafeCast<Promise<dynamic>>().await())
            } else {
                publish(purchases.getSharedInstance().changeUser(uid).unsafeCast<Promise<dynamic>>().await())
            }
        }.onFailure { println("RevenueCat web: identify failed: ${it.message}") }
    }

    private fun notIdentified() = Result.failure<Nothing>(BillingException(BillingException.Reason.STORE_ERROR, "RevenueCat web: no user identified"))

    private suspend fun packages(): Array<dynamic> {
        val offerings = instance().getOfferings().unsafeCast<Promise<dynamic>>().await()
        val all = offerings.all
        val chosen: dynamic = Plans.OFFERING_ID?.let { id -> if (all == null || all == undefined) null else all[id] }
        val offering = if (chosen == null || chosen == undefined) offerings.current else chosen
        return if (offering == null || offering == undefined) emptyArray() else offering.availablePackages.unsafeCast<Array<dynamic>>()
    }

    override suspend fun getOffers(): Result<List<PlanOffer>> {
        if (instance() == null) return notIdentified()
        return runCatching { packages().map { planOfferOf(it) } }.recoverCatching { throw it.toBillingException() }
    }

    override suspend fun purchase(offer: PlanOffer): Result<Unit> {
        val sdk = instance() ?: return notIdentified()
        val current = _customer.value.storePlan()?.entitlement
        // purchases-js 1.47 only starts subscriptions (its product changes, beta since 1.52, need a
        // subscriber token minted with a secret key). A running web plan is changed (upgrade now,
        // downgrade at renewal) in RevenueCat's customer portal, which offers only the paths set
        // under Product catalog -> Subscription changes; a plan from a store, in that store. Either
        // way the portal opens and the paywall explains (SH-52). A promotional grant is no store
        // plan: buying is an ordinary checkout.
        if (current != null && current.productId != offer.productId) {
            manageSubscription(_customer.value.managementUrl)
            return Result.failure(BillingException(BillingException.Reason.CHANGE_IN_PORTAL))
        }
        return runCatching {
            val pkg = packages().firstOrNull { it.identifier == offer.packageId }
                ?: throw BillingException(BillingException.Reason.OFFER_UNAVAILABLE)
            val params: dynamic = js("({})")
            params.rcPackage = pkg
            val result = sdk.purchase(params).unsafeCast<Promise<dynamic>>().await()
            publish(result.customerInfo)
        }.recoverCatching { throw it.toBillingException() }
    }

    override suspend fun restorePurchases(): Result<Unit> {
        // Web Billing purchases belong to the RevenueCat customer, not to a store account:
        // refreshing the customer info is the restore.
        val sdk = instance() ?: return notIdentified()
        return runCatching { publish(sdk.getCustomerInfo().unsafeCast<Promise<dynamic>>().await()) }
            .recoverCatching { throw it.toBillingException() }
    }

    override suspend fun manageSubscription(managementUrl: String?): Result<Unit> = runCatching {
        val store = _customer.value.storePlan()?.entitlement?.store
        val url = managementUrl ?: when (store) {
            BillingStore.PLAY_STORE -> "https://play.google.com/store/account/subscriptions"
            BillingStore.APP_STORE -> "https://apps.apple.com/account/subscriptions"
            else -> throw BillingException(BillingException.Reason.STORE_ERROR, "No management URL")
        }
        window.open(url, "_blank")
    }

    private fun Throwable.toBillingException(): BillingException {
        if (this is BillingException) return this
        val error = asDynamic()
        // purchases-js rejects with a PurchasesError (errorCode: ErrorCode) or, from the checkout,
        // a PurchaseFlowError (its own codes; the PurchasesError's code, if any, in purchasesErrorCode).
        val isFlowError = error.purchasesErrorCode != undefined || error.displayPurchaseFlowErrorCode != undefined
        if (isFlowError && error.purchasesErrorCode == undefined) {
            return BillingException(
                if (error.errorCode == 3) BillingException.Reason.NETWORK else BillingException.Reason.STORE_ERROR,
                message
            )
        }
        val code = if (isFlowError) error.purchasesErrorCode else error.errorCode
        val codes = RevenueCatJs.ErrorCode
        val reason = when {
            code == null || code == undefined -> BillingException.Reason.STORE_ERROR
            code == codes.UserCancelledError -> BillingException.Reason.CANCELLED
            code == codes.NetworkError -> BillingException.Reason.NETWORK
            code == codes.PurchaseNotAllowedError -> BillingException.Reason.NOT_ALLOWED
            code == codes.ProductNotAvailableForPurchaseError -> BillingException.Reason.OFFER_UNAVAILABLE
            else -> BillingException.Reason.STORE_ERROR
        }
        return BillingException(reason, message)
    }
}

private fun planOfferOf(pkg: dynamic): PlanOffer {
    val product = pkg.webBillingProduct ?: pkg.rcBillingProduct
    val packageId = pkg.identifier as String
    val productId = (product?.identifier as? String).orEmpty()
    val trial = product?.freeTrialPhase ?: product?.defaultSubscriptionOption?.trial
    val intro = product?.introPricePhase ?: product?.defaultSubscriptionOption?.introPrice
    return PlanOffer(
        packageId = packageId,
        productId = productId,
        tier = Plans.tierForPackage(packageId, productId),
        period = BillingPeriod.fromPackageType(pkg.packageType as? String)
            ?: BillingPeriod.fromIso(product?.normalPeriodDuration as? String),
        formattedPrice = (product?.currentPrice?.formattedPrice as? String).orEmpty(),
        introductoryOffer = when {
            trial != null && trial != undefined ->
                IntroductoryOffer(true, null, BillingDuration.fromIso(trial.periodDuration as? String))
            intro != null && intro != undefined ->
                IntroductoryOffer(false, intro.price?.formattedPrice as? String, BillingDuration.fromIso(intro.periodDuration as? String))
            else -> null
        }
    )
}
